import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { TwentyOne, GameEvent } from 'game-logic';
import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { CurrentViewModel } from './current-view-model';
import { CardView } from '../cards/card-view';
import { Positioning } from '../../model/positioning';
import { CardViewModel } from '../cards/card-view-model';
// import { DebugContainerVisual } from '../debug/debug-container-visual';

export class CurrentView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private cardViews: Map<number, CardView> = new Map();
  private destroyTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: CurrentViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.setName(CurrentView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.cardViewModelChanges$.subscribe((change) => {
        if (change.type === 'addFromDeck') {
          this.addFromDeck(scene, change.viewModel, change.duration, change.delay, change.instant);
        } else if (change.type === 'addFromNext') {
          this.addFromNext(scene, change.viewModel, change.duration, change.instant);
        } else if (change.type === 'removeToColumn') {
          this.removeToColumn(change.viewModel, change.duration, change.instant);
        } else if (change.type === 'removeToWaste') {
          this.removeToWaste(change.viewModel, change.duration);
        }
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    // Stop any tweens which are destroying child views
    this.destroyTweens.forEach((tween) => {
      tween.stop();
    });

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;

    this.cardViews.forEach((cardView) => cardView.onResize(resizeMetrics));
    this.refreshLayout(resizeMetrics);
  }

  private refreshLayout(resizeMetrics: ResizeMetrics): void {
    const centerRect = Positioning.globalCurrentCardRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );

    this.setSize(centerRect.width, centerRect.height);
    this.setPosition(centerRect.pos.x, centerRect.pos.y);

    Array.from(this.cardViews.values()).forEach((cardView) => {
      cardView.setPosition(0, 0);
    });
  }

  public async handleLogicEvents(event: GameEvent<TwentyOne.TwentyOneEvents>): Promise<void> {
    switch (event.type) {
      case 'onDrawDeckToCurrent':
        await this.viewModel.addCardFromDeck(
          event.payload.cardStackCurrent,
          event.payload.cardIndex,
          event.instant,
        );
        break;
      case 'onMoveCurrentToColumn':
        await this.viewModel.removeCardToColumn(event.payload.cardIndex, event.instant);
        break;
      case 'onMoveNextToCurrent':
        await this.viewModel.addCardFromNext(
          event.payload.cardStackCurrent,
          event.payload.cardIndex,
          event.instant,
        );
        break;
      default:
        break;
    }
  }

  private addFromDeck(
    scene: TwentyOneMainScene,
    cardViewModel: CardViewModel,
    duration: number,
    delay: number,
    instant: boolean,
  ): void {
    // Add the new card view
    const cardView = new CardView(scene, 0, 0, cardViewModel);
    if (this.currentResizeMetrics) {
      cardView.onResize(this.currentResizeMetrics);
    }
    this.add(cardView);

    this.cardViews.set(cardView.cardIndex, cardView);

    const startPt = Positioning.globalDeckPosition(
      this.viewModel.layoutConfig,
      this.currentResizeMetrics?.scalingMetrics,
    );
    const localStartPt = new Phaser.Math.Vector2();
    this.getLocalPoint(startPt.x, startPt.y, localStartPt);

    if (instant) {
      cardView.setPosition(0, 0);
      cardView.setScale(1.1, 1.1);
      cardView.flipCard(true, false, 1.1);
      return;
    }

    cardView.setPosition(localStartPt.x, localStartPt.y);
    cardView.setScale(0.75, 0.75);
    cardView.flipCard(true, true, 0.75);

    this.scene.tweens.add({
      targets: [cardView],
      x: 0,
      y: 0,
      scale: 1.1,
      delay: delay,
      duration: duration,
      ease: Phaser.Math.Easing.Cubic.InOut,
    });
  }

  private addFromNext(
    scene: TwentyOneMainScene,
    cardViewModel: CardViewModel,
    duration: number,
    instant: boolean,
  ): void {
    // Add the new card view
    const cardView = new CardView(scene, 0, 0, cardViewModel);
    if (this.currentResizeMetrics) {
      cardView.onResize(this.currentResizeMetrics);
    }
    this.add(cardView);
    this.cardViews.set(cardView.cardIndex, cardView);

    const layoutConfig = this.viewModel.layoutConfig;
    const startPt = Positioning.globalDeckPosition(
      layoutConfig,
      this.currentResizeMetrics?.scalingMetrics,
    );
    const localStartPt = new Phaser.Math.Vector2();
    this.getLocalPoint(startPt.x, startPt.y, localStartPt);

    if (instant) {
      cardView.setPosition(0, 0);
      cardView.setScale(1.1, 1.1);
      cardView.flipCard(true, false, 1.1);
      return;
    }

    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const nextOffsetX = layoutConfig.nextCardOffsetX * scaleFactor;
    const nextOffsetY = layoutConfig.nextCardOffsetY * scaleFactor;
    const nextAngle = layoutConfig.nextCardAngle;

    cardView.setPosition(localStartPt.x + nextOffsetX, localStartPt.y + nextOffsetY);
    cardView.setAngle(nextAngle);
    cardView.setScale(0.75, 0.75);
    cardView.flipCard(true, false, 0.75);

    this.scene.tweens.add({
      targets: [cardView],
      x: 0,
      y: 0,
      angle: 0,
      scale: 1.1,
      duration: duration,
      ease: Phaser.Math.Easing.Quintic.InOut,
    });
  }

  private removeToColumn(cardViewModel: CardViewModel, duration: number, instant: boolean): void {
    const foundView = this.cardViews.get(cardViewModel.cardIndex);
    if (foundView) {
      if (instant) {
        foundView.destroy();
      } else {
        const destroyTween = this.scene.tweens.add({
          targets: [foundView],
          duration: duration,
          scale: 0.25,
          alpha: 0,
          ease: Phaser.Math.Easing.Quadratic.Out,
          onComplete: () => {
            foundView.destroy();
          },
        });
        this.destroyTweens.push(destroyTween);
      }
    }
    this.cardViews.delete(cardViewModel.cardIndex);
  }

  private removeToWaste(cardViewModel: CardViewModel, duration: number): void {
    const foundView = this.cardViews.get(cardViewModel.cardIndex);
    if (foundView) {
      const disposePt = Positioning.globalWastePosition(
        this.viewModel.layoutConfig,
        this.currentResizeMetrics?.scalingMetrics,
      );
      const localDisposePt = new Phaser.Math.Vector2();
      this.getLocalPoint(disposePt.x, disposePt.y, localDisposePt);

      const destroyTween = this.scene.tweens.add({
        targets: [foundView],
        x: localDisposePt.x,
        y: localDisposePt.y,
        duration: duration,
        ease: Phaser.Math.Easing.Quadratic.Out,
        onComplete: () => {
          foundView.destroy();
        },
      });
      this.destroyTweens.push(destroyTween);
    }
    this.cardViews.delete(cardViewModel.cardIndex);
  }
}
